import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import moment from 'moment';
import { StorageService } from 'ngx-webstorage-service';

import { MODULE_ROUTES } from '@common/constants/module-routes.const';

import { PhxConstants } from '../model';
import { ApiService } from '../services/api.service';
import { AppStorageService } from '../services/app-storage.service';
import { AuthService } from '../services/auth.service';
import { IframeService } from '../services/iframe.service';
import { ToastService } from '../services/toast.service';

import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshingInProgress = false;
  private accessTokenSubject = new BehaviorSubject<string | null>(null);
  // Suppresses pre-emptive refresh after we issue a token. Re-enabled only when a 401
  // confirms the token actually expired, preventing cascades across request batches.
  private preemptiveSuppressed = false;
  private endpointsToIgnoreAuthError: Array<string> = ['Account/Logout'];
  private refreshTokenErrorKeys: Array<string> = ['invalid_grant', 'invalid_clientId'];

  // Cross-tab refresh lock: ensures only one browser tab hits the refresh endpoint at a time.
  private readonly refreshLockKey = 'fbo_auth_refresh_lock';
  private readonly refreshLockTimeoutMs = 10_000;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private toastService: ToastService,
    private iframeService: IframeService,
    @Inject(AppStorageService) private storageService: StorageService
  ) {}

  private static addDefaultHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return request;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken = this.storageService.get('BearerToken');
    const bearerTokenExpiresOn = this.storageService.get('BearerTokenExpiresOn');

    const twoMinutesFromNow = moment().add(2, 'm');
    const getTokenCall = req.url.indexOf('token') !== -1;
    if (!getTokenCall && !this.preemptiveSuppressed && bearerTokenExpiresOn && moment(bearerTokenExpiresOn) < twoMinutesFromNow) {
      return this.refreshToken(req, next);
    }

    return next.handle(TokenInterceptor.addDefaultHeaders(req, bearerToken)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          // in case of 401 http error
          if (err.status === HTTPResponseStatus.Unauthorized && !this.endpointsToIgnoreAuthError.some(endpoint => req.url.includes(endpoint))) {
            // get refresh tokens
            const refreshToken = this.storageService.get('RefreshToken');

            // if there are tokens then send refresh token request
            if (refreshToken && bearerToken) {
              // Token genuinely expired — re-enable pre-emptive refresh for the next cycle.
              this.preemptiveSuppressed = false;
              return this.refreshToken(req, next);
            }

            // otherwise logout and redirect to login page
            this.logoutAndRedirect();
          }

          // in following case (refresh token failed)
          if (err.status === HTTPResponseStatus.BadRequest && err.error && this.refreshTokenErrorKeys.includes(err.error.error)) {
            // logout and redirect to login page
            this.logoutAndRedirect();
          }
        }

        // if error has status neither 401 nor 400 then just return this error
        return throwError(() => err);
      })
    );
  }

  private logoutAndRedirect(): void {
    this.preemptiveSuppressed = false;
    this.authService.logout(false);
    // only show the toast message if user is logged in
    // we try to automatically log the person in from home page by using the refresh token to generate new access token
    // if failed to generate access token, user does not need to see the toast message
    if (this.authService.currentUser) {
      this.toastService.logWarning('Your session expired, please login again...');
    }
    if (this.iframeService.isIframe) {
      // Notify the parent so it can reload the iframe with a fresh token instead of showing the login page
      this.iframeService.notifyAuthRequired();
    }
    void this.router.navigate(['/', MODULE_ROUTES.LOGIN]);
  }

  /**
   * Tries to atomically acquire the cross-tab refresh lock.
   * An expired lock (older than refreshLockTimeoutMs) is silently stolen.
   *
   * @returns the unique owner token on success, or null if the lock is held.
   */
  private tryAcquireRefreshLock(): string | null {
    const existing = this.storageService.get(this.refreshLockKey) as string | null;
    if (existing != null) {
      const timestamp = parseInt(existing.split(':')[0], 10);
      if (!Number.isNaN(timestamp) && Date.now() - timestamp < this.refreshLockTimeoutMs) {
        // Lock held by another tab and not yet expired.
        return null;
      }
      // Lock is expired — proceed to steal it.
    }
    const owner = Math.random().toString(36).slice(2);
    const lockValue = `${Date.now()}:${owner}`;
    this.storageService.set(this.refreshLockKey, lockValue);
    // Re-read to confirm we won the race. If another tab wrote at the same
    // instant, the stored value will differ and we bail out.
    const stored = this.storageService.get(this.refreshLockKey) as string | null;
    return stored === lockValue ? owner : null;
  }

  /**
   * Releases the cross-tab refresh lock only if this tab still owns it.
   */
  private releaseRefreshLock(owner: string): void {
    const stored = this.storageService.get(this.refreshLockKey) as string | null;
    if (stored?.endsWith(`:${owner}`)) {
      this.storageService.remove(this.refreshLockKey);
    }
  }

  /**
   * Returns true when storage contains a token that does not expire within the
   * next two minutes — the same threshold used for the pre-emptive refresh.
   */
  private hasValidStoredToken(): boolean {
    const token = this.storageService.get('BearerToken') as string | null;
    const expiry = this.storageService.get('BearerTokenExpiresOn') as string | null;
    return !!(token && expiry && moment(expiry) >= moment().add(2, 'm'));
  }

  /**
   * Returns an Observable that resolves once the tab that currently holds the
   * refresh lock releases it. Uses storage events for cross-tab notification
   * and falls back to polling for same-tab MFE scenarios (storage events do not
   * fire in the originating tab).
   */
  private waitForOtherTabRefresh(): Observable<'refreshed' | 'failed' | 'timeout'> {
    return new Observable(subscriber => {
      let resolved = false;
      const refs: { timeout?: ReturnType<typeof setTimeout>; pollInterval?: ReturnType<typeof setInterval> } = {};

      const done = (result: 'refreshed' | 'failed' | 'timeout'): void => {
        if (resolved) {return;}
        resolved = true;
        clearTimeout(refs.timeout);
        clearInterval(refs.pollInterval);
        window.removeEventListener('storage', onStorage);
        subscriber.next(result);
        subscriber.complete();
      };

      const onStorage = (event: StorageEvent): void => {
        if (event.key === this.refreshLockKey && event.newValue === null) {
          done(this.hasValidStoredToken() ? 'refreshed' : 'failed');
        }
      };

      refs.timeout = setTimeout(() => done('timeout'), this.refreshLockTimeoutMs);

      // Guard 1 — lock already released before we register the listener.
      if (!this.storageService.get(this.refreshLockKey)) {
        done(this.hasValidStoredToken() ? 'refreshed' : 'failed');
        return;
      }

      window.addEventListener('storage', onStorage);

      // Guard 2 — lock released between Guard 1 and addEventListener().
      if (!this.storageService.get(this.refreshLockKey)) {
        done(this.hasValidStoredToken() ? 'refreshed' : 'failed');
        return;
      }

      // Polling fallback for same-tab scenarios where storage events are not fired.
      refs.pollInterval = setInterval(() => {
        if (!this.storageService.get(this.refreshLockKey)) {
          done(this.hasValidStoredToken() ? 'refreshed' : 'failed');
        }
      }, 200);

      return () => {
        clearTimeout(refs.timeout);
        clearInterval(refs.pollInterval);
        window.removeEventListener('storage', onStorage);
      };
    });
  }

  /**
   * Calls the refresh endpoint, updates the token subject, releases the lock,
   * and retries the original request. Falls back to any token another tab may
   * have written concurrently if the refresh endpoint itself returns an error.
   *
   * @param tokenBeforeRefresh - the stale token captured when refresh was initiated,
   *   used to detect a concurrent winner that wrote a newer token during our failure.
   */
  private performRefresh(request: HttpRequest<any>, next: HttpHandler, owner: string, tokenBeforeRefresh: string | null): Observable<HttpEvent<any>> {
    this.refreshingInProgress = true;
    this.accessTokenSubject.next(null);

    return this.apiService.refreshToken().pipe(
      switchMap(res => {
        this.refreshingInProgress = false;
        this.preemptiveSuppressed = true;
        this.releaseRefreshLock(owner);
        this.accessTokenSubject.next(res.access_token);
        // repeat failed request with new token
        return next.handle(TokenInterceptor.addDefaultHeaders(request, res.access_token));
      }),
      catchError(err => {
        this.refreshingInProgress = false;
        this.releaseRefreshLock(owner);

        // A concurrent refresh may have already succeeded and stored a new token.
        // If so, retry this request with that token instead of propagating the error
        // (which would cause invalid_grant to boot the user when a parallel refresh won the race).
        const latestToken = this.storageService.get('BearerToken') as string | null;
        if (latestToken && latestToken !== tokenBeforeRefresh) {
          this.preemptiveSuppressed = true;
          this.accessTokenSubject.next(latestToken);
          return next.handle(TokenInterceptor.addDefaultHeaders(request, latestToken));
        }

        // Definitive refresh failure with no concurrent winner — perform a clean logout
        // so the app is not left in a broken authenticated state.
        if (this.refreshTokenErrorKeys.includes(err.error?.error)) {
          this.logoutAndRedirect();
          return EMPTY;
        }

        return throwError(() => err);
      })
    );
  }

  private refreshToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Same-tab concurrent requests queue here until the in-progress refresh completes.
    if (this.refreshingInProgress) {
      return this.accessTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(TokenInterceptor.addDefaultHeaders(request, token)))
      );
    }

    // Re-read storage: another tab may have already refreshed since intercept() ran.
    // All tabs share localStorage, so one tab's refresh should satisfy the others
    // without an additional round-trip.
    const currentToken = this.storageService.get('BearerToken') as string | null;
    const currentExpiry = this.storageService.get('BearerTokenExpiresOn') as string | null;
    if (currentToken && currentExpiry && moment(currentExpiry) >= moment().add(2, 'm')) {
      this.preemptiveSuppressed = true;
      this.accessTokenSubject.next(currentToken);
      return next.handle(TokenInterceptor.addDefaultHeaders(request, currentToken));
    }

    const owner = this.tryAcquireRefreshLock();

    if (owner === null) {
      // Another tab holds the lock — wait for it to finish and use the result.
      return this.waitForOtherTabRefresh().pipe(
        switchMap(result => {
          if (result === 'refreshed') {
            const token = this.storageService.get('BearerToken') ?? '';
            this.accessTokenSubject.next(token);
            return next.handle(TokenInterceptor.addDefaultHeaders(request, token));
          }

          if (result === 'timeout') {
            // Lock expired without a valid token appearing. Try to steal the lock
            // and perform the refresh ourselves as a fallback.
            if (this.hasValidStoredToken()) {
              const token = this.storageService.get('BearerToken') ?? '';
              this.accessTokenSubject.next(token);
              return next.handle(TokenInterceptor.addDefaultHeaders(request, token));
            }
            const timeoutOwner = this.tryAcquireRefreshLock();
            if (timeoutOwner) {
              return this.performRefresh(request, next, timeoutOwner, currentToken);
            }
            // Another instance in this tab won the lock race; queue behind it.
            return this.accessTokenSubject.pipe(
              filter(token => token !== null),
              take(1),
              switchMap(token => next.handle(TokenInterceptor.addDefaultHeaders(request, token)))
            );
          }

          // result === 'failed': other tab released lock but token is still invalid.
          this.logoutAndRedirect();
          return throwError(() => new Error('Session expired'));
        })
      );
    }

    // Lock acquired — check once more before hitting the network. A concurrent
    // request cycle may have already refreshed the token (e.g. a slow in-flight
    // request whose 401 arrived after the refresh completed).
    if (this.hasValidStoredToken()) {
      this.releaseRefreshLock(owner);
      const token = this.storageService.get('BearerToken') ?? '';
      this.preemptiveSuppressed = true;
      this.accessTokenSubject.next(token);
      return next.handle(TokenInterceptor.addDefaultHeaders(request, token));
    }

    return this.performRefresh(request, next, owner, currentToken);
  }
}
